import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { CSVLink } from 'react-csv';
import { FilterBar, FilterSpec } from 'src/common/components/filter-bar';
import { Node } from 'src/common/interfaces/node.interface';
import { Journey } from 'src/common/interfaces/journey.interface';
import { JourneyAlias } from 'src/common/interfaces/journey/alias.interface';
import { TooltipComponent } from 'src/common/tooltip/component';
import { ContentDict, VisitorView } from 'src/editor/insights/constants';
import { DownloadStrongIcon } from 'src/monet/icons';
import { useCurrentOrganization } from 'src/store/organization';
import { apiGetJourneyAliases } from 'src/utils/journeyApi';
import { DashboardInsightsJourneysJourneyVisitorsTable } from './visitors-table';
import { DashboardInsightsJourneysJourneyStepsTable } from './steps-table';
import { TimePeriodParam } from '../../constants';
import { DashboardInsightsJourneysTabType } from '../constants';
import { DashboardInsightsJourneysJourneyTabs } from './tabs';
import { DashboardInsightsJourneysChatbotSessionsTable } from './chatbot-sessions-table';
import { formatDurationInHMS } from 'src/utils/date';
import { NeueLoadingSpinner } from 'src/editor/content-creation/components/loading/spinner';
import classNames from 'classnames';
import { buildInsightsViewDetails } from '../../helpers';
import dayjs from 'dayjs';
import _ from 'lodash';

type DashboardInsightsJourneysJourneyContentProps = {
  activeTabType: DashboardInsightsJourneysTabType;
  journey: Journey;
  timePeriodParam?: TimePeriodParam;
};

interface FormField {
  field_id: string;
  label: string;
  type: string;
  created_at: string;
  value: string;
}

interface FormResponse {
  aliasId: string;
  form: FormField[];
  created_at: string;
  owner_email: string;
}

interface ProcessedForm {
  aliasName: string;
  createdAt: string;
  ownerEmail: string;
  [key: string]: string;
}

export const DashboardInsightsJourneysJourneyContent: FC<DashboardInsightsJourneysJourneyContentProps> = ({
  activeTabType,
  journey,
  timePeriodParam,
}) => {
  const { currentOrganization } = useCurrentOrganization();

  const [isDownloadInProgress, setIsDownloadInProgress] = useState(false);
  const [aliases, setAliases] = useState<JourneyAlias[]>([]);
  const [visitorViews, setVisitorViews] = useState<any[]>([]);
  const [nativeForms, setNativeForms] = useState<any[]>([]);

  const csvLinkRef = useRef<any>(null);

  const allVisitorsFetcherRef = useRef<() => Promise<{ views: VisitorView[] }>>();
  const allNativeFormsFetcherRef = useRef<() => Promise<{ forms: any[] }>>();
  const allChatbotSessionsFetcherRef = useRef<() => Promise<{ events: any[] }>>();

  useEffect(() => {
    const fetchAliases = async () => {
      const data = await apiGetJourneyAliases(journey.uuid);
      setAliases(data.aliases as JourneyAlias[]);
    };
    fetchAliases();
  }, [currentOrganization.id, journey.id]);

  const contentDict: ContentDict = useMemo(() => {
    const cd: ContentDict = {};
    journey.steps.forEach((step: { nodes: Node[] }) =>
      step.nodes.forEach((node) => {
        cd[node.id] = node;
      })
    );

    return cd;
  }, [journey]);

  const contentList: Node[] = useMemo(() => {
    const cl: Node[] = [];
    journey.steps.forEach((step: { nodes: Node[] }) =>
      step.nodes.forEach((node) => {
        cl.push(node);
      })
    );

    return cl;
  }, [journey]);

  const filterSpecList: FilterSpec[] = useMemo(
    () => [
      {
        id: 'link',
        label: 'Link',
        type: 'multi-select',
        searchable: true,
        searchPlaceholder: 'Search for filter',
        options: aliases.map((alias) => ({
          label: alias.name,
          value: `${alias.id}`,
        })),
      },
      {
        id: 'step',
        label: 'Step',
        type: 'multi-select',
        searchable: true,
        searchPlaceholder: 'Search for filter',
        options: contentList.map((content) => ({
          label: content.name,
          value: `${content.id}`,
        })),
      },
    ],
    [aliases, contentList]
  );

  const onDownloadCsvClicked = (type: 'chatbot' | 'visitors' | 'native-forms') => async () => {
    if (type === 'native-forms') {
      if (!allNativeFormsFetcherRef.current || isDownloadInProgress) {
        return false;
      }
      setIsDownloadInProgress(true);
      try {
        const response = await allNativeFormsFetcherRef.current();
        if (!response.forms) {
          setIsDownloadInProgress(false);
          return;
        }

        const processedForms: ProcessedForm[] = [];

        response.forms.forEach((response: FormResponse) => {
          const { aliasId, form, created_at, owner_email } = response;

          const groupedByFieldId: { [fieldId: string]: FormField[] } = {};

          form.forEach((field: FormField) => {
            const fieldId = field.field_id;

            if (!groupedByFieldId[fieldId]) {
              groupedByFieldId[fieldId] = [];
            }

            groupedByFieldId[fieldId].push(field);
          });

          const latestLabels: { [fieldId: string]: string } = {};
          Object.entries(groupedByFieldId).forEach(([fieldId, fields]) => {
            latestLabels[fieldId] = fields[fields.length - 1].label;
          });

          const maxSubmissions = Math.max(...Object.values(groupedByFieldId).map((fields) => fields.length));

          for (let submissionIndex = 0; submissionIndex < maxSubmissions; submissionIndex++) {
            const formsObject: { [key: string]: string } = {};
            let hasAnyValue = false;
            let submissionCreatedAt = dayjs(created_at);

            Object.entries(groupedByFieldId).forEach(([fieldId, fields]) => {
              const currentLabel = latestLabels[fieldId];

              if (fields[submissionIndex]) {
                const field = fields[submissionIndex];
                const value = field.value;

                if (value && value.trim() !== '') {
                  formsObject[currentLabel] = value;
                  hasAnyValue = true;

                  if (field.created_at) {
                    submissionCreatedAt = dayjs(field.created_at);
                  }
                }
              }
            });

            if (hasAnyValue) {
              processedForms.push({
                aliasName: aliasId,
                ...formsObject,
                createdAt: submissionCreatedAt.format('YYYY-MM-DD HH:mm:ss'),
                ownerEmail: owner_email,
              });
            }
          }
        });

        const sortedForms = _.chain(processedForms)
          .groupBy('aliasName')
          .mapValues((aliasGroup) => _.orderBy(aliasGroup, [(form) => dayjs(form.createdAt)], ['desc']))
          .toPairs()
          .orderBy([(pair) => dayjs(_.maxBy(pair[1], (form) => dayjs(form.createdAt))!.createdAt)], ['desc'])
          .flatMap(([, pair]) => pair)
          .value();
        setNativeForms(sortedForms);
        setIsDownloadInProgress(false);
        csvLinkRef.current.link.click();
      } catch (err) {
        console.log('Download err', err);
        setIsDownloadInProgress(false);
      }
      return;
    }

    if (type === 'visitors') {
      if (!allVisitorsFetcherRef.current || isDownloadInProgress) {
        return false;
      }
      setIsDownloadInProgress(true);
      try {
        const response = await allVisitorsFetcherRef.current();
        if (!response.views) {
          setIsDownloadInProgress(false);
          return;
        }
        // Consider processing data in chunks if it's too large
        const processedViews = response.views.map((view) => {
          const {
            alias_id,
            completed_percentage,
            created_at,
            duration,
            email,
            ip_address,
            location,
            session_replay_uuid,
          } = view;
          const builtDetails = buildInsightsViewDetails(view);
          const viewerName = builtDetails?.name;
          const companyName = builtDetails?.company?.name;
          const deviceType = builtDetails?.device_type?.os;
          let { custom_field_values } = builtDetails?.visitor || {};
          if (Array.isArray(custom_field_values) === false && typeof custom_field_values === 'object') {
            custom_field_values = Object.values(custom_field_values);
            custom_field_values = custom_field_values.filter((a: any) => typeof a !== 'string');
          }
          const phoneNumber = custom_field_values?.find((field: any) => field.field === 'phone_number')?.value || '';

          return {
            name: viewerName,
            viewed: created_at,
            time_spent: duration ? formatDurationInHMS(duration) : '-',
            completion: `${Math.round(completed_percentage)}%`,
            email_address: email,
            company: companyName,
            phone_number: phoneNumber,
            location,
            ip_address,
            alias: alias_id,
            device: deviceType,
            session_replay_url: `https://app.journey.io/sessions/${session_replay_uuid}`,
          };
        });

        setVisitorViews(processedViews);

        if (!csvLinkRef) {
          console.log('No link');
          return;
        }
        setIsDownloadInProgress(false);
        csvLinkRef.current.link.click();
      } catch (err) {
        console.log('Download err', err);
        setIsDownloadInProgress(false);
      }
    } else {
      if (!allChatbotSessionsFetcherRef.current || isDownloadInProgress) {
        return false;
      }
      setIsDownloadInProgress(true);
      try {
        const response = await allChatbotSessionsFetcherRef.current();
        if (!response.events) {
          setIsDownloadInProgress(false);
          return;
        }
        setVisitorViews(response.events.map(buildInsightsViewDetails));
        if (!csvLinkRef) {
          return;
        }
        setIsDownloadInProgress(false);
        csvLinkRef.current.link.click();
      } catch (err) {
        setIsDownloadInProgress(false);
      }
    }
  };

  const csvFileName = useMemo(() => {
    if (activeTabType === 'visitor-sessions') {
      return 'journey-visitor-sessions.csv';
    }
    if (activeTabType === 'chatbot-sessions') {
      return 'journey-chatbot-sessions.csv';
    }
    if (activeTabType === 'steps') {
      return 'journey-forms.csv';
    }
  }, []);

  const onClickCSVDownloadHandler = (type: typeof activeTabType) => {
    if (type === 'visitor-sessions') {
      return onDownloadCsvClicked('visitors');
    } else if (type === 'chatbot-sessions') {
      return onDownloadCsvClicked('chatbot');
    }
    return onDownloadCsvClicked('native-forms');
  };

  const getCSVDownloaderTooltipContent = (type: typeof activeTabType) => {
    if (type === 'visitor-sessions') {
      return 'Download visitor sessions';
    } else if (type === 'chatbot-sessions') {
      return 'Download chatbot sessions';
    }
    return 'Download native form submissions';
  };

  const getCSVLinkData = (type: typeof activeTabType) => {
    if (type === 'steps') {
      return nativeForms;
    }

    return visitorViews;
  };

  const renderCSVDownloadButtonContent = (type: typeof activeTabType) => {
    if (type === 'steps') {
      return <span className='text-bedrock-p text-bedrock-black'>Download native form submissions</span>;
    }

    return (
      <DownloadStrongIcon
        className={classNames('text-bedrock-black', {
          'cursor-pointer': !isDownloadInProgress,
        })}
      />
    );
  };

  return (
    <>
      <div className='flex space-x-md divide-x divide-bedrock-gray-medium'>
        <DashboardInsightsJourneysJourneyTabs journeyUuid={journey.uuid} activeTab={activeTabType} />
        {activeTabType === 'steps' && <FilterBar searchPlaceholder='Search step name' widthClassname='w-[150px]' />}
        {['visitor-sessions', 'chatbot-sessions', 'steps'].includes(activeTabType) && (
          <>
            <FilterBar
              filterSpecList={filterSpecList}
              searchPlaceholder='Search visitor email'
              widthClassname='w-[150px]'
            />
            <div className='px-md flex items-center text-bedrock-black cursor-pointer'>
              <TooltipComponent
                placement='top'
                tooltipContent={getCSVDownloaderTooltipContent(activeTabType)}
                theme='journey'
                styling='tooltip'
              >
                <CSVLink
                  data={getCSVLinkData(activeTabType)}
                  filename={csvFileName}
                  className='hidden'
                  ref={csvLinkRef}
                  target='_blank'
                />

                <button
                  onClick={onClickCSVDownloadHandler(activeTabType)}
                  className={classNames({ 'pointer-events-none disabled': isDownloadInProgress })}
                >
                  {isDownloadInProgress ? (
                    <NeueLoadingSpinner size='16' theme='default' />
                  ) : (
                    renderCSVDownloadButtonContent(activeTabType)
                  )}
                </button>
              </TooltipComponent>
            </div>
          </>
        )}
      </div>
      <div className='grow flex flex-col mt-6'>
        {activeTabType === 'steps' ? (
          <DashboardInsightsJourneysJourneyStepsTable
            contentDict={contentDict}
            contentList={contentList}
            journey={journey}
            timePeriodParam={timePeriodParam}
            setAllNativeFormsFetcher={(fetcher) => {
              allNativeFormsFetcherRef.current = fetcher;
            }}
          />
        ) : activeTabType === 'visitor-sessions' ? (
          <DashboardInsightsJourneysJourneyVisitorsTable
            journey={journey}
            timePeriodParam={timePeriodParam}
            setVisitorViews={setVisitorViews}
            setAllVisitorsFetcher={(fetcher) => {
              allVisitorsFetcherRef.current = fetcher;
            }}
          />
        ) : activeTabType === 'chatbot-sessions' ? (
          <DashboardInsightsJourneysChatbotSessionsTable
            journey={journey}
            timePeriodParam={timePeriodParam}
            setAllChatbotSessionsFetcher={(fetcher) => {
              allChatbotSessionsFetcherRef.current = fetcher;
            }}
          />
        ) : null}
      </div>
    </>
  );
};
